import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getPostsByHashtag } from '../services/hashtagService';
import PostCard from '../components/posts/PostCard';
import { HashtagIcon } from '@heroicons/react/24/outline';

import { useTranslation } from 'react-i18next';
import SEOHead from '../components/seo/SEOHead';
import StructuredData from '../components/seo/StructuredData';
import { getHashtagUrl, getFullUrl } from '../utils/urlUtils';



const HashtagPage: React.FC = () => {
  const { tag } = useParams<'tag'>();
  const [posts, setPosts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  useTranslation();

  // Formatear el hashtag para mostrar
  const displayTag = tag?.startsWith('#') ? tag : `#${tag}`;

  useEffect(() => {
    const fetchPosts = async () => {
      if (!tag) return;

      try {
        setLoading(true);
        setError(null);

        const hashtagPosts = await getPostsByHashtag(tag);
        setPosts(hashtagPosts);
      } catch (error) {
        console.error(`Error fetching posts for hashtag ${tag}:`, error);
        setError(`No se pudieron cargar los posts con el hashtag ${displayTag}`);
      } finally {
        setLoading(false);
      }
    };

    fetchPosts();
  }, [tag, displayTag]);

  // Preparar datos para SEO
  const hashtagUrl = getFullUrl(getHashtagUrl(tag || ''));
  const pageTitle = `${displayTag} | DomiRed`;
  const pageDescription = `Explora publicaciones con el hashtag ${displayTag} en DomiRed. Descubre contenido relacionado y únete a la conversación.`;

  return (
    <div className="max-w-2xl mx-auto px-4 py-6">
      {/* Reemplazamos Helmet con nuestro componente SEO mejorado */}
      <SEOHead
        title={pageTitle}
        description={pageDescription}
        url={hashtagUrl}
        type="website"
      />

      {/* Structured Data para el hashtag como WebPage */}
      <StructuredData
        type="WebSite"
        data={{
          name: `${displayTag} - DomiRed`,
          description: pageDescription,
          url: hashtagUrl,
          potentialAction: {
            "@type": "SearchAction",
            "target": `https://domired.top/search?q=${encodeURIComponent(tag || '')}`,
            "query-input": "required name=search_term"
          }
        }}
      />

      <div className="mb-6">
        <Link to="/" className="text-domired-600 dark:text-domired-400 hover:underline mb-4 inline-block">
          &larr; Volver al inicio
        </Link>

        <div className="flex items-center mt-2">
          <HashtagIcon className="h-6 w-6 mr-2 text-domired-600 dark:text-domired-400" />
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{displayTag}</h1>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-domired-600"></div>
        </div>
      ) : error ? (
        <div className="bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200 p-4 rounded-md">
          {error}
        </div>
      ) : posts.length === 0 ? (
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
          No hay publicaciones con el hashtag {displayTag}.
        </div>
      ) : (
        <div className="space-y-4">
          {posts.map((post) => (
            <PostCard key={post.id} post={post} />
          ))}
        </div>
      )}
    </div>
  );
};

export default HashtagPage;
